/**
 * المرحلة 1.3 — finding and booking a doctor: the rules the screens need.
 *
 * Backend/lib/discovery.js holds the rules the server applies; this file
 * mirrors the part a screen works out before it asks: which channels a
 * doctor can be booked for, what a video or voice visit costs, which
 * specialties are close to one another, and what a profile still lacks.
 * Tests/suites/frontend/p13-completeness.test.mjs holds both copies to the
 * same answers. The server stays the judge: a screen that guessed wrong is
 * refused there.
 *
 * `bookingPolicy(locale)` reads GET /api/auth/capabilities `locale.booking`
 * and falls back, field by field, to each control's default — today's
 * behaviour — so a screen with no answer yet behaves exactly as before 1.3.
 */

export const MODES = Object.freeze(['in_person', 'video', 'voice']);
export const SORTS = Object.freeze(['none', 'soonest', 'rating', 'price']);
export const DOC_KINDS = Object.freeze(['licence', 'syndicate', 'id', 'specialty']);
export const PROFILE_FIELDS = Object.freeze(['bio', 'languages', 'qualifications', 'experience', 'photo']);
export const LANGUAGE_CODES = Object.freeze(['ar', 'en', 'fr', 'de', 'es', 'it', 'tr', 'ru', 'ur', 'hi']);

/** The icon each channel shows on a card, a badge and a chooser. */
export const MODE_ICONS = Object.freeze({ in_person: 'building-2', video: 'video', voice: 'phone' });

/** The same pairs as the server's (bookingJourneys.matchScope: related). */
export const RELATED_PAIRS = Object.freeze([
  ['family', 'internal'], ['family', 'pedatric'], ['internal', 'cardio'], ['internal', 'gastro'],
  ['internal', 'chest'], ['internal', 'nephrology'], ['internal', 'rheuma'], ['neuro', 'psych'],
  ['derma', 'rheuma'], ['ortho', 'physio'], ['gyneco', 'urology'], ['nutrition', 'internal'],
]);

export function relatedOf(categoryId) {
  const id = String(categoryId || '');
  const out = [];
  for (const [a, b] of RELATED_PAIRS) {
    if (a === id && !out.includes(b)) out.push(b);
    if (b === id && !out.includes(a)) out.push(a);
  }
  return out;
}

/** The channels a doctor may be booked for: the policy's, that the doctor offers. */
export function allowedModes(policyModes, doctorModes) {
  const policy = Array.isArray(policyModes) && policyModes.length ? policyModes : ['in_person'];
  const own = Array.isArray(doctorModes) && doctorModes.length ? doctorModes : ['in_person'];
  return MODES.filter((m) => policy.includes(m) && own.includes(m));
}

/** A visit's price in a channel: video and voice are a share of the clinic's price (100 = the same). */
export function modePrice(base, mode, policy = {}) {
  const price = Math.max(0, Math.round(Number(base) || 0));
  const percent = mode === 'video' ? Number(policy.videoPercent) : mode === 'voice' ? Number(policy.voicePercent) : 100;
  if (!Number.isFinite(percent) || percent <= 0) return price;
  return Math.round((price * percent) / 100);
}

const filled = (v) => v !== null && v !== undefined && String(v).trim() !== '';

/** The doctorOnboarding.profileRequired fields a profile still lacks. */
export function profileMissing(profile = {}, required = []) {
  const p = profile || {};
  const has = {
    bio: filled(p.bioAr) || filled(p.bioEn),
    languages: Array.isArray(p.languages) && p.languages.length > 0,
    qualifications: Array.isArray(p.qualifications) && p.qualifications.length > 0,
    experience: Number.isFinite(Number(p.yearsOfExperience)) && p.yearsOfExperience !== null && Number(p.yearsOfExperience) >= 0,
    photo: filled(p.image),
  };
  return (required || []).filter((field) => !has[field]);
}

const pick = (value, allowed, dflt) => (allowed.includes(value) ? value : dflt);
const num = (value, dflt, min, max) => {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return dflt;
  return Math.min(max, Math.max(min, Math.round(n)));
};

/**
 * The booking policies of the caller's scope, whole: what each screen reads
 * before it offers a sort, a channel, a reschedule or the hour-first search.
 */
export function bookingPolicy(locale) {
  const b = (locale && locale.booking) || {};
  const modes = Array.isArray(b.modes) ? MODES.filter((m) => b.modes.includes(m)) : [];
  const r = b.reschedule || {};
  const v = b.reviews || {};
  const q = b.queue || {};
  return {
    journeys: pick(b.journeys, ['doctor', 'slot', 'both'], 'doctor'),
    familyEntry: pick(b.familyEntry, ['hidden', 'show'], 'hidden'),
    matchScope: pick(b.matchScope, ['same', 'related'], 'same'),
    defaultSort: pick(b.defaultSort, SORTS, 'none'),
    showPrice: pick(b.showPrice, ['yes', 'no'], 'yes'),
    modes: modes.length ? modes : ['in_person'],
    videoPercent: num(b.videoPercent, 100, 10, 200),
    voicePercent: num(b.voicePercent, 100, 10, 200),
    reschedule: {
      allowed: pick(r.allowed, ['no', 'yes'], 'no'),
      hoursBefore: num(r.hoursBefore, 24, 1, 168),
      maxTimes: num(r.maxTimes, 1, 1, 5),
      deposit: pick(r.deposit, ['carry', 'forfeit'], 'carry'),
    },
    reviews: {
      visibility: pick(v.visibility, ['patients', 'admins'], 'patients'),
      moderation: pick(v.moderation, ['before', 'none'], 'before'),
      minCount: num(v.minCount, 3, 1, 50),
    },
    queue: { confirm: pick(q.confirm, ['auto', 'doctor'], 'auto'), lapseHours: num(q.lapseHours, 0, 0, 168) },
  };
}

/** Whether the patient may book by the hour first in this scope. */
export const slotFirstOpen = (policy) => policy.journeys === 'slot' || policy.journeys === 'both';

/** Whether the doctor-first list is offered (always, unless the scope books by the hour only). */
export const doctorFirstOpen = (policy) => policy.journeys !== 'slot';

/**
 * Whether «تغيير الميعاد» is offered on a booking, and why not. The server
 * decides; this only keeps a button that would be refused off the screen.
 *
 * @param {object} booking  { date, time, status, rescheduledFrom }
 * @param {object} policy   bookingPolicy(...).reschedule
 * @param {Date}   [now]
 * @returns {{ ok: boolean, reason: null | 'closed' | 'status' | 'late' | 'limit' }}
 */
export function rescheduleState(booking, policy, now = new Date()) {
  if (!policy || policy.allowed !== 'yes') return { ok: false, reason: 'closed' };
  if (!booking || !['pending', 'confirmed'].includes(String(booking.status))) return { ok: false, reason: 'status' };
  const moves = Array.isArray(booking.rescheduledFrom) ? booking.rescheduledFrom.length : 0;
  if (moves >= policy.maxTimes) return { ok: false, reason: 'limit' };
  const startsAt = startOf(booking);
  if (startsAt && (startsAt.getTime() - now.getTime()) / 3600000 < policy.hoursBefore) return { ok: false, reason: 'late' };
  return { ok: true, reason: null };
}

/** The visit's start as a Date in the hospital's zone (Cairo, UTC+2/+3 — read from the offset of that day). */
export function startOf(booking) {
  if (!booking || !/^\d{4}-\d{2}-\d{2}$/.test(String(booking.date || '')) || !/^\d{1,2}:\d{2}/.test(String(booking.time || ''))) return null;
  const [h, m] = String(booking.time).split(':').map(Number);
  const guess = new Date(`${booking.date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00Z`);
  const offset = zoneOffsetMinutes(guess, 'Africa/Cairo');
  return new Date(guess.getTime() - offset * 60000);
}

function zoneOffsetMinutes(date, timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'));
    return Math.round((asUtc - date.getTime()) / 60000);
  } catch {
    return 120;
  }
}

/** Is a visit due to start: today, within the next 15 minutes or already gone by (as the server's doctorQueue.isDue). */
export function isDue(booking, clock) {
  if (!booking || String(booking.date) !== clock.today) return false;
  if (!['confirmed', 'in_progress'].includes(String(booking.status))) return false;
  const [h, m] = String(booking.time || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return false;
  return h * 60 + m <= clock.minute + 15;
}

/** Today in the hospital's zone and the minute of the day, as the server's clock(). */
export function hospitalClock(now = new Date(), timeZone = 'Africa/Cairo') {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type).value;
    return { today: `${get('year')}-${get('month')}-${get('day')}`, minute: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
  } catch {
    return { today: now.toISOString().slice(0, 10), minute: now.getUTCHours() * 60 + now.getUTCMinutes() };
  }
}

/** 'YYYY-MM-DD' plus n days, by the calendar. */
export function addDays(dateKey, n) {
  const d = new Date(`${dateKey}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** A rating as a card shows it: «4.6» with one decimal, or null. */
export const ratingText = (avg) => (avg === null || avg === undefined || !Number.isFinite(Number(avg)) ? null : Number(avg).toFixed(1));
